/**
 * Formatting for scoped operations (targeted re-summarize / partial sync).
 *
 * Free of React so it can be unit tested. The wording here matters: the backend
 * records an operation to SSM at **submit** time, before any ticket has been
 * processed, so the text must describe work that has been *queued* rather than
 * work that is done. The previous "Last: resummarize · 42 tickets · 3s ago" read
 * as a completed run when nothing had happened yet.
 */
import { LastOperation } from '../../api/tickets';

const OPERATION_LABELS: Record<string, string> = {
  resummarize: 'Re-summarize',
  sync: 'Partial sync',
};

export function operationLabel(type: string): string {
  return OPERATION_LABELS[type] ?? type;
}

export function timeAgo(iso: string, now: number = Date.now()): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const secs = Math.floor((now - then) / 1000);
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

/**
 * One-line summary of the most recent scoped operation, or null if none.
 *
 * A negative count means the endpoint reported no number (partial sync does not),
 * so the count is omitted rather than shown as zero. Zero itself is kept: "0
 * tickets enqueued" is the useful signal that a scope matched nothing.
 */
export function lastOperationText(
  op: LastOperation | null,
  now: number = Date.now(),
): string | null {
  if (!op) return null;
  const parts = [operationLabel(op.type), op.scope];
  if (typeof op.count === 'number' && op.count >= 0) {
    parts.push(`${op.count} tickets enqueued`);
  }
  parts.push(`submitted ${timeAgo(op.at, now)}`);
  return parts.join(' · ');
}
